import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ACHIEVEMENT_DEFINITIONS, getAchievementByType } from '@/components/membership/achievements/achievementDefinitions';

interface SellerAchievement {
  id: string;
  seller_id: string;
  achievement_type: string;
  unlocked_at: string;
  notified: boolean;
}

interface SellerStats {
  totalVentas: number;
  ventasMes: number;
  totalCanjes: number;
  totalAhorros: number;
  trainingCompleted: boolean;
  ranking: number;
}

export const useSellerAchievements = () => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<SellerAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAchievement, setNewAchievement] = useState<string | null>(null);
  const [currentAchievementId, setCurrentAchievementId] = useState<string | null>(null);
  const [totalPoints, setTotalPoints] = useState(0);

  const fetchAchievements = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('seller_achievements')
        .select('*')
        .eq('seller_id', user.id)
        .order('unlocked_at', { ascending: false });

      if (error) throw error;

      setAchievements(data || []);

      // Calculate total points
      const points = (data || []).reduce((sum, a) => {
        const def = getAchievementByType(a.achievement_type);
        return sum + (def?.points || 0);
      }, 0);
      setTotalPoints(points);

      // Check for unnotified achievements - NO marcar como notified aquí
      // Se marca como notified cuando el usuario cierra el modal
      const unnotified = (data || []).find(a => !a.notified);
      if (unnotified) {
        setNewAchievement(unnotified.achievement_type);
        setCurrentAchievementId(unnotified.id);
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const checkAndGrantAchievements = useCallback(async (stats: SellerStats) => {
    if (!user) return;

    const unlockedTypes = new Set(achievements.map(a => a.achievement_type));
    const toGrant: string[] = [];

    // Check PERMANENT achievements only (cycle milestones are handled separately in seller_cycle_stats)
    // Permanent: sales_50, sales_100, redemptions_500, savings_100k, savings_500k, savings_1m, training_complete
    
    // Sales achievements (permanent - total across all time)
    if (stats.totalVentas >= 50 && !unlockedTypes.has('sales_50')) {
      toGrant.push('sales_50');
    }
    if (stats.totalVentas >= 100 && !unlockedTypes.has('sales_100')) {
      toGrant.push('sales_100');
    }
    
    // Redemptions achievement (permanent - requires 500 total)
    if (stats.totalCanjes >= 500 && !unlockedTypes.has('redemptions_500')) {
      toGrant.push('redemptions_500');
    }
    
    // Savings achievements (permanent - total across all time)
    if (stats.totalAhorros >= 100000 && !unlockedTypes.has('savings_100k')) {
      toGrant.push('savings_100k');
    }
    if (stats.totalAhorros >= 500000 && !unlockedTypes.has('savings_500k')) {
      toGrant.push('savings_500k');
    }
    if (stats.totalAhorros >= 1000000 && !unlockedTypes.has('savings_1m')) {
      toGrant.push('savings_1m');
    }
    
    // Training achievement (permanent)
    if (stats.trainingCompleted && !unlockedTypes.has('training_complete')) {
      toGrant.push('training_complete');
    }
    
    // NOTE: Cycle milestones (first_sale, strong_start, consistency, consecutive_3, 
    // volume_bronce_10, volume_plata_10, volume_oro_10, top_saver, podium, first_place)
    // are tracked in seller_cycle_stats and their points are calculated via cycle_milestone_points
    // in the RPC. They are NOT stored in seller_achievements.

    // Grant achievements
    for (const type of toGrant) {
      try {
        const { error } = await supabase
          .from('seller_achievements')
          .insert({
            seller_id: user.id,
            achievement_type: type,
            notified: false
          });

        if (!error) {
          console.log(`Achievement granted: ${type}`);
        }
      } catch (err) {
        console.error(`Error granting achievement ${type}:`, err);
      }
    }

    // Refresh if any were granted
    if (toGrant.length > 0) {
      await fetchAchievements();
    }
  }, [user, achievements, fetchAchievements]);

  const dismissNewAchievement = useCallback(async () => {
    // Marcar como notified en la BD cuando el usuario cierra el modal
    if (currentAchievementId) {
      try {
        await supabase
          .from('seller_achievements')
          .update({ notified: true })
          .eq('id', currentAchievementId);
      } catch (err) {
        console.error('Error marking achievement as notified:', err);
      }
      setCurrentAchievementId(null);
    }
    setNewAchievement(null);
  }, [currentAchievementId]);

  // Check and revoke achievements that no longer meet requirements
  const checkAndRevokeAchievements = useCallback(async (stats: SellerStats) => {
    if (!user) return;

    const toRevoke: string[] = [];

    for (const achievement of achievements) {
      const type = achievement.achievement_type;
      let shouldRevoke = false;

      // Only revoke PERMANENT achievements (not cycle milestones)
      switch (type) {
        case 'sales_50':
          shouldRevoke = stats.totalVentas < 50;
          break;
        case 'sales_100':
          shouldRevoke = stats.totalVentas < 100;
          break;
        case 'redemptions_500':
          shouldRevoke = stats.totalCanjes < 500;
          break;
        case 'savings_100k':
          shouldRevoke = stats.totalAhorros < 100000;
          break;
        case 'savings_500k':
          shouldRevoke = stats.totalAhorros < 500000;
          break;
        case 'savings_1m':
          shouldRevoke = stats.totalAhorros < 1000000;
          break;
        case 'training_complete':
          shouldRevoke = !stats.trainingCompleted;
          break;
        // Cycle milestones are not stored here, they're in seller_cycle_stats
        // If any old cycle achievements exist, don't revoke them automatically
        default:
          shouldRevoke = false;
          break;
      }

      if (shouldRevoke) {
        toRevoke.push(achievement.id);
      }
    }

    // Delete achievements that no longer meet requirements
    if (toRevoke.length > 0) {
      for (const id of toRevoke) {
        try {
          await supabase
            .from('seller_achievements')
            .delete()
            .eq('id', id);
          console.log(`Achievement revoked: ${id}`);
        } catch (err) {
          console.error(`Error revoking achievement ${id}:`, err);
        }
      }
      
      await fetchAchievements(); // Refresh achievements list
    }
  }, [user, achievements, fetchAchievements]);

  const getAchievementBadges = useCallback(() => {
    return achievements.map(a => getAchievementByType(a.achievement_type)?.badge || '').filter(Boolean);
  }, [achievements]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  return {
    achievements,
    loading,
    newAchievement,
    totalPoints,
    checkAndGrantAchievements,
    checkAndRevokeAchievements,
    dismissNewAchievement,
    getAchievementBadges,
    refetch: fetchAchievements
  };
};

export default useSellerAchievements;
